import { apiClient } from "./client";

// Release 1.6.0 (O-R72): the Planning Calendar (GET api/v1/planning/calendar/*, policy OperationsData; 404 while the
// switch planning.calendar.enabled is off). Mirrors SummitOperations.Contracts/PlanningCalendar/PlanningCalendarDtos.cs.
// Scheduled figures are computed, GSS facts recorded, VIPER's words predicted. Read-only.

export type CalendarBand = "under" | "healthy" | "tight" | "overloaded" | "unknown";
export type CalendarBlockStatus = "late" | "critical" | "hot" | "normal";
export type CalendarPlan = "current" | "baseline";

export interface CalendarContextDto {
  plan: string;
  planLabel: string;
  planHash: string;
  statusDate: string;
  generatedAtUtc: string;
  frozenDays: string[];
  type: string | null;
  typeLabel: string;
  shop: string | null;
  machine: string | null;
  scopeRestricted: boolean;
  scopeNote: string | null;
  comparedWith: string | null;
  scenarioId: number | null;
  notes: string[];
  valueKind: string;
}

export interface CalendarTypeOptionDto {
  key: string;
  label: string;
  machineCount: number;
}

export interface CalendarShopOptionDto {
  code: string;
  name: string;
}

export interface CalendarMachineDto {
  code: string;
  label: string;
  shopCode: string | null;
  type: string;
}

export interface CalendarOptionsDto {
  types: CalendarTypeOptionDto[];
  shops: CalendarShopOptionDto[];
  machines: CalendarMachineDto[];
}

export interface CalendarMachineDayDto {
  code: string;
  loadHours: number;
  capacityHours: number;
  percent: number | null;
  band: CalendarBand;
  down: boolean;
}

export interface CalendarMonthDayDto {
  date: string;
  inMonth: boolean;
  isWorkday: boolean;
  holiday: string | null;
  isToday: boolean;
  isFrozen: boolean;
  loadHours: number;
  capacityHours: number;
  percent: number | null;
  band: CalendarBand;
  overloadHours: number;
  overloadedMachines: number;
  machines: CalendarMachineDayDto[];
  operationsStarting: number;
  operationsFinishing: number;
  shipDue: number;
  shipDueCustomers: string[];
  late: number;
  atRisk: number;
  downMachines: string[];
  moved: number;
  isPulse: boolean;
  /** Release 1.6.x (O-R87): planned against accomplished, for a day before the plan's status date. */
  history?: CalendarHistoryCellDto | null;
}

export interface CalendarWeekSummaryDto {
  isoWeek: number;
  weekStart: string;
  loadHours: number;
  capacityHours: number;
  percent: number | null;
  band: CalendarBand;
  bottleneckMachine: string | null;
  bottleneckPercent: number | null;
  overloadMachineDays: number;
  history?: CalendarHistoryCellDto | null;
}

export interface CalendarMonthWeekDto {
  summary: CalendarWeekSummaryDto;
  days: CalendarMonthDayDto[];
}

export interface CalendarPulseDto {
  date: string;
  loadHours: number;
  capacityHours: number;
  percent: number;
  overloadHours: number;
  why: string;
  ruleId: string;
}

export interface PlanningCalendarMonthDto {
  month: string;
  context: CalendarContextDto;
  options: CalendarOptionsDto;
  machines: CalendarMachineDto[];
  weeks: CalendarMonthWeekDto[];
  pulse: CalendarPulseDto | null;
  totalLoadHours: number;
  totalCapacityHours: number;
}

/** Where a moved operation sat in the compared plan: the published baseline, or the current plan under a scenario. */
export interface CalendarGhostDto {
  date: string;
  startHour: number;
  endHour: number;
  machine: string | null;
  deltaWorkdays: number;
  overlay: "baseline" | "scenario" | string;
}

export interface CalendarBlockDto {
  operationKey: string;
  workOrderKey: string;
  workOrder: string;
  sequence: number;
  partNumber: string;
  description: string | null;
  machine: string;
  date: string;
  fromHour: number;
  toHour: number;
  hours: number;
  setupHours: number;
  runHours: number;
  piece: number;
  pieces: number;
  startDay: string;
  startHour: number;
  finishDay: string;
  finishHour: number;
  totalHours: number;
  priority: string;
  status: CalendarBlockStatus;
  healthTier: string | null;
  isLate: boolean;
  slackWorkdays: number;
  materialBlocked: boolean;
  ospBlocked: boolean;
  blockedBy: string[];
  isFrozen: boolean;
  inProcess: boolean;
  constraint: string;
  why: string;
  needBy: string | null;
  shipDate: string | null;
  ghost: CalendarGhostDto | null;
}

export interface CalendarLaneDayDto {
  date: string;
  capacityHours: number;
  loadHours: number;
  percent: number | null;
  band: CalendarBand;
  overloadHours: number;
  down: boolean;
  history?: CalendarHistoryCellDto | null;
}

export interface CalendarLaneDto {
  machine: CalendarMachineDto;
  days: CalendarLaneDayDto[];
  blocks: CalendarBlockDto[];
  /** O-R87: recorded work (direct punches) on the lane's past days. */
  actualBlocks?: CalendarActualBlockDto[];
}

export interface CalendarDaySummaryDto {
  date: string;
  isWorkday: boolean;
  holiday: string | null;
  isToday: boolean;
  isFrozen: boolean;
  loadHours: number;
  capacityHours: number;
  percent: number | null;
  band: CalendarBand;
  operations: number;
  overloadedMachines: number;
  history?: CalendarHistoryCellDto | null;
}

export interface PlanningCalendarWeekDto {
  weekStart: string;
  isoWeek: number;
  context: CalendarContextDto;
  options: CalendarOptionsDto;
  days: CalendarDaySummaryDto[];
  lanes: CalendarLaneDto[];
  pulse: CalendarPulseDto | null;
  totalLoadHours: number;
  totalCapacityHours: number;
}

export interface CalendarDayBlockDto {
  block: CalendarBlockDto;
  quantity: number | null;
  customerName: string | null;
  operator: string | null;
  materialReadiness: string;
  ospReadiness: string;
  nextSequence: number | null;
  nextMachine: string | null;
  nextDescription: string | null;
}

export interface CalendarDayLaneDto {
  machine: CalendarMachineDto;
  capacityHours: number;
  loadHours: number;
  percent: number | null;
  band: CalendarBand;
  overloadHours: number;
  down: boolean;
  blocks: CalendarDayBlockDto[];
  history?: CalendarHistoryCellDto | null;
  actualBlocks?: CalendarActualBlockDto[];
}

export interface CalendarQueueItemDto {
  operationKey: string;
  workOrder: string;
  sequence: number;
  partNumber: string;
  machine: string;
  hours: number;
  status: CalendarBlockStatus;
}

export interface CalendarRiskDto {
  kind: "late" | "material" | "osp" | "overload" | "down" | string;
  text: string;
  workOrderKey: string | null;
  machine: string | null;
}

export interface PlanningCalendarDayDto {
  date: string;
  context: CalendarContextDto;
  options: CalendarOptionsDto;
  summary: CalendarDaySummaryDto;
  lanes: CalendarDayLaneDto[];
  nextWorkday: string | null;
  nextDayQueue: CalendarQueueItemDto[];
  risks: CalendarRiskDto[];
  operatorsShown: boolean;
  /** O-R87: the day is before the plan's status date (it shows what happened). */
  isPast?: boolean;
  slips?: CalendarSlipDto[];
}

/** O-R87: planned against accomplished. Band: on-plan | behind | well-behind | unplanned | no-plan. */
export interface CalendarHistoryCellDto {
  /** snapshot (the plan recorded that day) | baseline | mixed | none (actuals only) */
  planSource: string;
  planLabel: string;
  plannedHours: number;
  actualHours: number;
  adherencePercent: number | null;
  band: string;
  plannedOperations: number;
  plannedFinishes: number;
  finished: number;
  pieces: number;
  slips: number;
  actualPercentOfCapacity: number | null;
  actualValueKind: string;
}

export interface CalendarActualBlockDto {
  date: string;
  machine: string;
  workOrderKey: string | null;
  workOrder: string;
  sequence: number | null;
  partNumber: string | null;
  hours: number;
  pieces: number;
  valueKind: string;
}

export interface CalendarSlipDto {
  operationKey: string;
  workOrderKey: string;
  workOrder: string;
  sequence: number | null;
  partNumber: string | null;
  machine: string;
  plannedFinish: string;
  closedOn: string | null;
}

/** O-R88: one insight fact; horizon past | present | future. */
export interface CalendarInsightFactDto {
  horizon: string;
  key: string;
  text: string;
  valueKind: string;
  link: string | null;
}

export interface PlanningCalendarInsightsDto {
  view: string;
  from: string;
  to: string;
  typeLabel: string;
  plan: string;
  pastFrom: string | null;
  pastTo: string | null;
  pastBorrowed: boolean;
  futureFrom: string;
  futureTo: string;
  futureBorrowed: boolean;
  facts: CalendarInsightFactDto[];
  narrative: string;
  narrativeValueKind: string;
  narrativeIsAiWritten: boolean;
  narrativeFallbackReason: string | null;
  auditId: string | null;
  suggestions: { text: string; link: string }[];
  peopleShown: boolean;
  generatedAtUtc: string;
}

export interface PlanningCalendarDayNarrativeDto {
  date: string;
  type: string | null;
  text: string;
  valueKind: string;
  isAiWritten: boolean;
  fallbackReason: string | null;
  auditId: string | null;
  generatedAtUtc: string;
}

/** The filters every view shares (all optional; the server applies the caller's scope). */
export interface CalendarFilters {
  type: string;
  shop: string;
  wc: string;
  plan: CalendarPlan;
  changes: boolean;
  scenario: number | null;
}

function params(f: CalendarFilters, extra: Record<string, string>): Record<string, string> {
  const p: Record<string, string> = { ...extra };
  if (f.type) p.type = f.type;
  if (f.shop) p.shop = f.shop;
  if (f.wc) p.wc = f.wc;
  if (f.plan === "baseline") p.plan = "baseline";
  if (f.changes) p.changes = "true";
  if (f.scenario !== null) p.scenario = String(f.scenario);
  return p;
}

const filterKey = (f: CalendarFilters) => [f.type, f.shop, f.wc, f.plan, f.changes, f.scenario] as const;

export const PLANNING_CALENDAR_QK = {
  availability: ["planning", "calendar", "availability"] as const,
  month: (month: string, f: CalendarFilters) => ["planning", "calendar", "month", month, ...filterKey(f)] as const,
  week: (date: string, f: CalendarFilters) => ["planning", "calendar", "week", date, ...filterKey(f)] as const,
  day: (date: string, f: CalendarFilters) => ["planning", "calendar", "day", date, ...filterKey(f)] as const,
  narrative: (date: string, f: CalendarFilters) => ["planning", "calendar", "narrative", date, f.type, f.shop, f.wc, f.plan] as const,
  insights: (view: string, date: string, f: CalendarFilters) => ["planning", "calendar", "insights", view, date, f.type, f.shop, f.wc, f.plan] as const
};

export const planningCalendarApi = {
  availability: async (): Promise<{ enabled: boolean }> => (await apiClient.get<{ enabled: boolean }>("/planning/calendar/availability")).data,
  month: async (month: string, f: CalendarFilters): Promise<PlanningCalendarMonthDto> =>
    (await apiClient.get<PlanningCalendarMonthDto>("/planning/calendar/month", { params: params({ ...f, scenario: null }, { month }) })).data,
  week: async (date: string, f: CalendarFilters): Promise<PlanningCalendarWeekDto> =>
    (await apiClient.get<PlanningCalendarWeekDto>("/planning/calendar/week", { params: params(f, { date }) })).data,
  day: async (date: string, f: CalendarFilters): Promise<PlanningCalendarDayDto> =>
    (await apiClient.get<PlanningCalendarDayDto>("/planning/calendar/day", { params: params(f, { date }) })).data,
  narrative: async (date: string, f: CalendarFilters): Promise<PlanningCalendarDayNarrativeDto> =>
    (await apiClient.get<PlanningCalendarDayNarrativeDto>("/planning/calendar/day/summary", { params: params({ ...f, changes: false, scenario: null }, { date }) })).data,
  insights: async (view: string, date: string, f: CalendarFilters): Promise<PlanningCalendarInsightsDto> =>
    (await apiClient.get<PlanningCalendarInsightsDto>("/planning/calendar/insights", { params: params({ ...f, changes: false, scenario: null }, { view, date }) })).data
};
